import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Card from "components/Card";
import Button from "components/Button";
import { getAppraisalCycleAPI, generateCycleReportAPI } from "api/apiHelper";
import { AppraisalCycle } from "types/appraisal-cycle-types";
import { MdArrowBack, MdEdit, MdDownload } from "react-icons/md";
import { toast } from "react-toastify";

const ViewCycle = () => {
  const { cycleId } = useParams<{ cycleId: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [cycle, setCycle] = useState<AppraisalCycle | null>(null);

  useEffect(() => {
    if (cycleId) {
      fetchCycleData();
    }
  }, [cycleId]);

  const fetchCycleData = async () => {
    try {
      setIsLoading(true);
      const response = await getAppraisalCycleAPI(Number(cycleId));
      setCycle(response.data);
    } catch (error: any) {
      console.error("Error fetching cycle:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const calculateTotalWeightage = () => {
    let total = 0;
    if (cycle?.enable_self_review) total += parseFloat(cycle.self_review_weightage);
    if (cycle?.enable_manager_review) total += parseFloat(cycle.manager_review_weightage);
    if (cycle?.enable_peer_review) total += parseFloat(cycle.peer_review_weightage);
    if (cycle?.enable_client_review) total += parseFloat(cycle.client_review_weightage);
    return total;
  };

  const handleDownloadReport = async () => {
    if (!cycleId || !cycle) return;
    
    if (!cycle.locked) {
      toast.error("Cycle must be locked before generating reports");
      return;
    }

    try {
      const blob = await generateCycleReportAPI(Number(cycleId));
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `appraisal_report_${cycle.name.replace(/\s+/g, '_')}_${cycleId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success("Report downloaded successfully");
    } catch (error: any) {
      toast.error(error?.response?.data?.error || "Failed to generate report");
    }
  };

  if (isLoading) {
    return (
      <div className="mt-5 flex items-center justify-center py-12">
        <p className="text-gray-500 dark:text-gray-400">Loading cycle...</p>
      </div>
    );
  }

  if (!cycle) {
    return (
      <div className="mt-5 flex items-center justify-center py-12">
        <p className="text-red-500 dark:text-red-400">Cycle not found</p>
      </div>
    );
  }

  return (
    <div className="mt-5">
      <div className="mb-6 flex items-center justify-between">
        <Button
          varient="secondary"
          onClick={() => navigate("/admin-appraisals?tab=1")}
          className="flex items-center gap-2 px-4 py-2"
        >
          <MdArrowBack className="h-5 w-5" />
          Back
        </Button>
        <div className="flex items-center gap-2">
          {cycle.locked && (
            <Button
              varient="primary"
              onClick={handleDownloadReport}
              className="flex items-center gap-2 px-6 py-2"
            >
              <MdDownload className="h-5 w-5" />
              Download Report
            </Button>
          )}
          <Button
            varient="primary"
            onClick={() => navigate(`/admin-appraisals/cycles/${cycleId}/edit`)}
            className="flex items-center gap-2 px-6 py-2"
          >
            <MdEdit className="h-5 w-5" />
            Edit Cycle
          </Button>
        </div>
      </div>

      <Card extra="p-6 mb-6">
        <div className="space-y-4">
          <div>
            <h2 className="text-2xl font-bold text-navy-700 dark:text-white mb-2">
              {cycle.name}
            </h2>
            <div className="flex items-center gap-3">
              <span
                className={`px-3 py-1 rounded-full text-sm font-semibold ${
                  cycle.is_active
                    ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                    : "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                }`}
              >
                {cycle.is_active ? "Active" : "Inactive"}
              </span>
              {cycle.locked && (
                <span className="px-3 py-1 rounded-full text-sm font-semibold bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400">
                  Locked
                </span>
              )}
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Created: {new Date(cycle.created_at).toLocaleDateString()}
              </span>
            </div>
          </div>

          {cycle.description && (
            <div>
              <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-300 mb-2">
                Description
              </h3>
              <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                {cycle.description}
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Form Opens</p>
              <p className="text-base font-medium text-navy-700 dark:text-white">
                {new Date(cycle.form_opens).toLocaleString()}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Deadline</p>
              <p className="text-base font-medium text-navy-700 dark:text-white">
                {new Date(cycle.deadline).toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      </Card>

      <Card extra="p-6 mb-6">
        <h3 className="text-xl font-bold text-navy-700 dark:text-white mb-4">
          Review Types & Weightage
        </h3>
        <div className="space-y-4">
          {cycle.enable_self_review && (
            <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-navy-700/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-navy-700 dark:text-white">Self Review</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Template: {cycle.self_review_template_name || "N/A"}
                  </p>
                </div>
                <span className="px-3 py-1 rounded text-sm font-semibold bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                  {parseFloat(cycle.self_review_weightage).toFixed(1)}%
                </span>
              </div>
            </div>
          )}

          {cycle.enable_manager_review && (
            <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-navy-700/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-navy-700 dark:text-white">Manager Review</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Template: {cycle.manager_review_template_name || "N/A"}
                  </p>
                </div>
                <span className="px-3 py-1 rounded text-sm font-semibold bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                  {parseFloat(cycle.manager_review_weightage).toFixed(1)}%
                </span>
              </div>
            </div>
          )}

          {cycle.enable_peer_review && (
            <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-navy-700/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-navy-700 dark:text-white">Peer Review</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Template: {cycle.peer_review_template_name || "N/A"}
                  </p>
                </div>
                <span className="px-3 py-1 rounded text-sm font-semibold bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                  {parseFloat(cycle.peer_review_weightage).toFixed(1)}%
                </span>
              </div>
            </div>
          )}

          {cycle.enable_client_review && (
            <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-navy-700/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-navy-700 dark:text-white">Client Review</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Template: {cycle.client_review_template_name || "N/A"}
                  </p>
                </div>
                <span className="px-3 py-1 rounded text-sm font-semibold bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                  {parseFloat(cycle.client_review_weightage).toFixed(1)}%
                </span>
              </div>
            </div>
          )}

          <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-gray-600 dark:text-gray-400">
                Total Weightage
              </p>
              <p className="text-xl font-bold text-navy-700 dark:text-white">
                {calculateTotalWeightage().toFixed(1)}%
              </p>
            </div>
          </div>
        </div>
      </Card>

      <Card extra="p-6">
        <h3 className="text-xl font-bold text-navy-700 dark:text-white mb-4">
          Participants ({cycle.participants.length})
        </h3>
        {cycle.participants.length === 0 ? (
          <p className="text-center text-gray-500 dark:text-gray-400">
            No participants added to this cycle yet.
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {cycle.participants.map((participant) => (
              <div
                key={participant.id}
                className="p-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-navy-800"
              >
                <p className="text-sm font-medium text-navy-700 dark:text-white">
                  {participant.full_name}
                </p>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default ViewCycle;
